import { apiClient } from './client'
import type {
  HospitalImport,
  HospitalImportListResponse,
  ValidationResult,
  ImportResult,
  ExportFilters,
  ImportHistoryParams,
} from '@/types/hospital-import'

export const hospitalImportApi = {
  /**
   * Export hospitals to CSV file
   * @param filters - Optional filters for export (state, participant status)
   * @returns CSV file blob
   */
  async exportHospitals(filters?: ExportFilters): Promise<Blob> {
    const response = await apiClient.get('/hospitals/export', {
      params: filters,
      responseType: 'blob',
    })
    return response.data
  },

  /**
   * Download empty CSV template for hospital import
   * @returns CSV template blob
   */
  async downloadTemplate(): Promise<Blob> {
    const response = await apiClient.get('/hospitals/import/template', {
      responseType: 'blob',
    })
    return response.data
  },

  /**
   * Validate CSV file without importing
   * @param file - CSV file to validate
   * @returns Validation result with errors and warnings
   */
  async validateImport(file: File): Promise<ValidationResult> {
    const formData = new FormData()
    formData.append('file', file)

    const response = await apiClient.post<{ success: boolean; data: ValidationResult }>(
      '/hospitals/import/validate',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return response.data.data
  },

  /**
   * Import hospitals from CSV file
   * @param file - CSV file to import
   * @param dryRun - If true, validate without importing
   * @param skipInvalidRows - If true, skip rows with validation errors and import valid rows
   * @returns Import result with counts and errors
   */
  async importHospitals(
    file: File,
    dryRun: boolean = false,
    skipInvalidRows: boolean = false
  ): Promise<ImportResult> {
    const formData = new FormData()
    formData.append('file', file)
    if (dryRun) {
      formData.append('dryRun', 'true')
    }
    if (skipInvalidRows) {
      formData.append('skipInvalidRows', 'true')
    }

    const response = await apiClient.post<{ success: boolean; data: ImportResult }>(
      '/hospitals/import',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    )
    return response.data.data
  },

  /**
   * Get import history with pagination
   * @param params - Pagination parameters
   * @returns List of past imports
   */
  async getImportHistory(params?: ImportHistoryParams) {
    const response = await apiClient.get<{
      success: boolean
      data: HospitalImportListResponse
    }>('/hospitals/imports', {
      params,
    })
    return response.data
  },

  /**
   * Get specific import details by ID
   * @param id - Import ID (UUID)
   * @returns Import details
   */
  async getImportById(id: string) {
    const response = await apiClient.get<{ success: boolean; data: HospitalImport }>(
      `/hospitals/imports/${id}`
    )
    return response.data
  },
}
